import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../store/auth.store';
import toast from 'react-hot-toast';
import { OrbaryxLogo } from '../components/OrbaryxLogo';

export function Login() {
  const navigate = useNavigate();
  const { login, tenantSlug: savedSlug } = useAuthStore();

  const [form, setForm] = useState({ email: '', password: '', tenantSlug: savedSlug || '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.tenantSlug) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    setError('');

    try {
      await login(form.email.trim(), form.password, form.tenantSlug.toLowerCase().trim());
      navigate('/');
      toast.success('Welcome back!');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || '';
      if (msg.includes('timeout') || msg.includes('ECONNREFUSED') || msg.includes('Network')) {
        setError('Server unavailable. Please try again in a moment.');
      } else if (msg.includes('locked')) {
        setError(msg);
      } else {
        setError('Invalid credentials. Check your organization slug, email, and password.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-dark-0 flex items-center justify-center p-4">
      {/* Background gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-orbayx-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <OrbaryxLogo size={72} showText={false} />
          </div>
          <h1 className="text-2xl font-bold text-white">Orbaryx</h1>
          <p className="text-white/50 text-sm mt-1">IT Runs Itself</p>
        </div>

        {/* Card */}
        <div className="bg-surface-dark-1 border border-white/10 rounded-2xl p-8">
          <h2 className="text-lg font-semibold text-white mb-6">Sign in to your account</h2>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-4 text-sm text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-white/60 block mb-1.5 font-medium">Organization Slug</label>
              <input
                type="text"
                value={form.tenantSlug}
                onChange={(e) => setForm((f) => ({ ...f, tenantSlug: e.target.value.toLowerCase() }))}
                placeholder="your-org"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-orbayx-500/50 focus:bg-white/8 transition-all"
              />
            </div>

            <div>
              <label className="text-xs text-white/60 block mb-1.5 font-medium">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="admin@company.com"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-orbayx-500/50 focus:bg-white/8 transition-all"
              />
            </div>

            <div>
              <label className="text-xs text-white/60 block mb-1.5 font-medium">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="••••••••••••"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-11 text-sm text-white placeholder-white/30 focus:outline-none focus:border-orbayx-500/50 focus:bg-white/8 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-orbayx-600 hover:bg-orbayx-500 rounded-xl text-sm font-semibold text-white transition-all shadow-glow disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : 'Sign in'}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-white/5 text-center">
            <p className="text-xs text-white/30">
              Supports SSO · SAML 2.0 · OIDC
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-white/20 mt-6">
          © {new Date().getFullYear()} Orbayx. Enterprise Automation Platform.
        </p>
      </div>
    </div>
  );
}
